// PRD-024 F3.4 — shared presentation helpers for the spreadsheet Proposal /
// Agreement worksheet. One grid template is shared across the column header,
// every Outcome's economics line, the inline cost lines, and the footer, so
// every money column scans straight down. House style (load-bearing): the
// Proposal Editor and the signed Agreement render on the SAME grid.
//
// PRD-024 moves Sell Config INTO each Outcome row: every Outcome carries its
// own Sell Unit Name · Unit of Measure · Price · Qty → its own Revenue; parent
// totals are the SUM of the children. There is no allocation %.

// Column grid templates from the ratified mockup (`--econ`,
// mockup-worksheet-est-actual-columns.html, PRD-026 F3). The PROPOSAL stage is
// 8 columns — no Actual column exists pre-signing:
//   Sell unit | Unit of measure | Price | Qty | Revenue | Est. cost | Margin | %
// Cost / Margin / % only render when cost is visible (seller, or a shared
// P&L) — Principle #6. The buyer never sees Cost/Margin/% (FR-12).
export const GRID_WITH_COST =
  'minmax(150px,1.5fr) 128px 86px 82px 120px 120px 120px 58px';
// AGREEMENT stage — 9 columns: the Actual track APPEARS at signing, so
// Estimated (frozen, amber) and Actual (live, teal) each own a column:
//   Sell unit | Unit of measure | Price | Qty | Revenue | Estimated | Actual | Margin | %
export const GRID_WITH_COST_AGREEMENT =
  'minmax(146px,1.5fr) 118px 78px 74px 106px 102px 102px 106px 56px';
//   Sell unit | Unit of measure | Price | Qty | Total price   (buyer / no-cost)
export const GRID_NO_COST =
  'minmax(150px,1.5fr) 128px 86px 82px 120px';

export const gridStyle = (showCost, mode = 'proposal') => ({
  display: 'grid',
  gridTemplateColumns: !showCost
    ? GRID_NO_COST
    : (mode === 'agreement' ? GRID_WITH_COST_AGREEMENT : GRID_WITH_COST),
  gap: '12px',
  alignItems: 'center',
});

export const fmtUSD = (cents) => {
  const n = Math.round((Number(cents) || 0) / 100);
  return `$${n.toLocaleString('en-US')}`;
};

export const fmtUSDc = (cents) => {
  const n = (Number(cents) || 0) / 100;
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  // Accounting convention for negatives (house style, matches formatCents).
  return n < 0 ? `($${abs})` : `$${abs}`;
};

export const fmtQty = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 4 });

export const fmtPct = (pct) => `${Math.round(Number(pct) || 0)}%`;

// PRD-031 F3.1 — header percentages carry one decimal (header call sites
// only; the grid keeps an integer %).
export const fmtPct1 = (pct) => `${(Number(pct) || 0).toFixed(1)}%`;

// Margin %-chip color, light-theme equivalents of the mockup's good/ok/bad:
// ≥40% healthy · 20–40% thin · <20% or negative warning. Presentation
// defaults — tunable later, not configurable in v1.
export function marginChipClass(pct) {
  const p = Number(pct) || 0;
  if (p < 20) return 'bg-red-100 text-red-700';
  if (p < 40) return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
}

// PRD-024 FR-7 — completeness gate (replaces the retired allocation gate).
// A row is complete when it has a Sell Unit Name, a Unit-of-Measure id, a
// positive Price, and a positive Quantity. The Proposal is signable only when
// every Outcome is complete (mirrors `economics.signable` server-side).
export function rowComplete(row) {
  return Boolean(
    row.sellUnitName && String(row.sellUnitName).trim()
    && row.sellUnitOfMeasureId
    && (Number(row.sellPricePerUnitCents) || 0) > 0
    && (Number(row.sellQuantity) || 0) > 0
  );
}

export function completenessStatus(rows) {
  if (rows.length === 0) return { empty: true, complete: false, incompleteCount: 0 };
  const incompleteCount = rows.filter((r) => !rowComplete(r)).length;
  return { empty: false, complete: incompleteCount === 0, incompleteCount };
}

/* ============================================================================
   PRD-031 Cluster E — worksheet grid v2 (economics-row-final.html, ratified).
   ============================================================================ */

// E2a — the role × mode column contract. Each configuration declares its own
// fixed-track total; the two prose tracks share fractions summing to exactly
// 1 so table-layout:fixed can never redistribute slack into a money track.
// Track px sizes are the ratified mockup's, measured under the embedded
// fonts against max(widest body value, <tfoot> rollup, header label).
const F_SELL = 0.615;
const F_UOM = 0.385;

// IBM Plex Mono is metric-stable: every glyph advances 0.6em, so a money
// string's rendered width is computable without measurement — which is what
// lets the E4 track rule ("max(widest value across all rows, the rollup)")
// bind against REAL data, not just the mockup's seed. The ratified mockup
// widths below are MINIMA; a dataset with wider values grows its track.
const MONO_EM = 0.6;
const monoW = (str, fontPx) => (str ? String(str).length * MONO_EM * fontPx : 0);

// `h` is the visible label; `a` is the ACCESSIBLE NAME carrying the
// qualifier that used to render as a second visual line ("Estimated cost,
// frozen at signing"). Every `a` contains its `h` verbatim (WCAG 2.5.3).
// `rows`/`totals` (worksheet shapes) feed the E4 sizing rule; omitted, the
// ratified minima apply.
export function colsFor({ mode, showCost, isBuyer, rows = [], totals = null }) {
  const isAgreement = mode === 'agreement';
  const revH = isBuyer ? 'Total price' : 'Revenue';
  const c = [
    { k: 'sell', f: F_SELL, h: 'Sell unit', a: 'Sell unit' },
    { k: 'uom', f: F_UOM, h: 'Unit of measure', a: 'Unit of measure' },
    { k: 'price', w: 77, font: 12.5, h: 'Price', a: 'Price per unit', vals: (r) => fmtUSDc(r.sellPricePerUnitCents) },
    { k: 'qty', w: 64, font: 12.5, h: 'Quantity', a: 'Quantity', vals: (r) => fmtQty(r.sellQuantity) },
    { k: 'rev', w: 95, font: 13, h: revH, a: revH, vals: (r) => fmtUSDc(r.revenueCents), tot: (t) => fmtUSDc(t.revenueCents) },
  ];
  // P&L tracks are ABSENT FROM THE DOM when cost is not visible — never
  // CSS-hidden (a hidden cost column is a data leak one inspection away).
  if (showCost) {
    c.push({ k: 'est', w: 92, font: 12.5, h: 'Estimated cost', a: 'Estimated cost, frozen at signing', vals: (r) => fmtUSDc(r.estimatedCostCents), tot: (t) => fmtUSDc(t.estimatedCostCents) });
    if (isAgreement) {
      c.push(
        { k: 'act', w: 95, font: 13, h: 'Actual cost', a: 'Actual cost, live', vals: (r) => fmtUSDc(r.costCents), tot: (t) => fmtUSDc(t.costCents) },
        // The rollup foots its own column: Σ(live margins) = revenue − actual
        // (the mockup's Agreement-total row and the Realized-margin hero are
        // the same number).
        { k: 'marg', w: 95, font: 13, h: 'Margin', a: 'Margin, live', vals: (r) => fmtUSDc(r.marginCents != null ? r.marginCents : (r.revenueCents || 0) - (r.costCents || 0)), tot: (t) => fmtUSDc((t.revenueCents || 0) - (t.costCents || 0)) },
        { k: 'pct', w: 41, font: 13, h: '%', a: 'Margin %', vals: (r) => `${Math.round(r.marginPct || 0)}%`, tot: (t) => `${t.revenueCents > 0 ? Math.round(((t.revenueCents - (t.costCents || 0)) / t.revenueCents) * 100) : 0}%` },
      );
    }
  }
  // No delivery status exists pre-signing, so no Status column on Proposals.
  if (isAgreement) c.push({ k: 'stat', w: 152, h: 'Status', a: 'Status' });

  // E4 — every fixed track fits its widest value INCLUDING the rollup, plus
  // its own paddings and ~3px headroom. The last column carries the wide
  // right gutter (26px chevron gutter on Agreements, 15px on Proposals).
  const lastIdx = c.length - 1;
  c.forEach((col, i) => {
    if (!col.vals) return;
    let widest = 0;
    for (const r of rows) widest = Math.max(widest, monoW(col.vals(r), col.font));
    if (col.tot && totals) widest = Math.max(widest, monoW(col.tot(totals), col.font));
    const padR = i === lastIdx ? (isAgreement ? 26 : 15) : 3;
    col.w = Math.max(col.w, Math.ceil(widest + 3 + padR + 3));
  });
  return c;
}

export const fixedOf = (cols) => cols.reduce((a, c) => a + (c.w || 0), 0);

// The measure cap sends surplus width to the container margin instead of
// stretching the sheet into a dead prose gap: 1160px for the 10-track
// configurations, 840px for the narrow (≤6-track) ones.
export const MEASURE_CAP_WIDE = 1160;
export const MEASURE_CAP_NARROW = 840;
export const measureCapFor = (cols) => (cols.length >= 10 ? MEASURE_CAP_WIDE : MEASURE_CAP_NARROW);

// WU-B (staging fix-it sprint 2026-08) — the ratified FLOOR, the other half
// of the measure contract. economics-row-final.html fixes the 10-track
// card floor at 1120px (711px of fixed tracks + banked prose headroom);
// below it the header band wraps into broken words ("UNIT OF MEASU RE")
// and prose crushes — the "floating headers / cramped numbers" staging
// defects. The page container enforces it: the card never compresses
// below the floor; a narrower window pans instead (sticky headers ride
// vertical scroll, unaffected by body-level horizontal pan).
export const MEASURE_FLOOR_WIDE = 1120;
export const MEASURE_FLOOR_NARROW = 640;
export const measureFloorFor = (cols) => (cols.length >= 10 ? MEASURE_FLOOR_WIDE : MEASURE_FLOOR_NARROW);

// F7a plain status words + the ratified E5c VERB MAP + the E6 per-word tone
// map. One source of truth: the Status track is sized against this list, the
// design-token guard probes every entry at its own weight, and every rendered
// annotation verb derives from it — no row can carry a hand-written verb.
export const DELIVERY_STATUS = {
  delivered_confirmed:            { word: 'Delivered',          cls: 's-del',    verb: 'delivered' },
  delivered_pending_confirmation: { word: 'Delivered',          cls: 's-del',    verb: 'delivered' },
  not_delivered:                  { word: 'Not delivered',      cls: 's-no',     verb: 'checked' },
  needs_human_review:             { word: 'Needs human review', cls: 's-rev',    verb: 'flagged' },
  behind:                         { word: 'Behind',             cls: 's-behind', verb: 'checked' },
  on_track:                       { word: 'On track',           cls: 's-ok',     verb: 'checked' },
  checking_now:                   { word: 'Checking now',       cls: 's-chk',    verb: 'started' },
  awaiting_evidence:              { word: 'Awaiting evidence',  cls: 's-wait',   verb: 'requested' },
  not_measurable_yet:             { word: 'Not measurable yet', cls: 's-nmy',    verb: 'starts' },
};

const STAMP_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export function fmtStamp(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  let h = d.getHours();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${STAMP_MONTHS[d.getMonth()]} ${d.getDate()}, ${h}:${String(d.getMinutes()).padStart(2, '0')} ${ampm}`;
}
export function fmtStampDay(iso) {
  if (!iso) return null;
  // Date-only strings (active_from) must not shift a day through the local
  // timezone: parse them as plain dates.
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
  if (m) return `${STAMP_MONTHS[+m[2] - 1]} ${+m[3]}`;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${STAMP_MONTHS[d.getMonth()]} ${d.getDate()}`;
}

// E5c — the annotation names the event that established the status. The verb
// derives from the map; the moment comes from the row's own data. E5b — on
// the Client view, a Delivered Outcome still awaiting the Client's
// acceptance replaces the plain timestamp with the acceptance-pending
// string; the status WORD stays identical across roles.
export function deliveryStamp(row, { isBuyer } = {}) {
  const st = row.delivery?.status;
  const s = DELIVERY_STATUS[st];
  if (!s) return null;
  if (st === 'not_measurable_yet') {
    const day = fmtStampDay(row.activeFrom);
    return day ? `starts ${day}` : 'starts with its measurement window';
  }
  const momentIso = (st === 'delivered_confirmed' || st === 'delivered_pending_confirmation')
    ? (row.verifiedAt || row.delivery?.last_checked_at)
    : (row.delivery?.last_checked_at || null);
  if (isBuyer && s.word === 'Delivered' && row.buyerSignoff === 'awaiting_buyer') {
    const day = fmtStampDay(momentIso);
    return `delivered${day ? ` ${day}` : ''} · awaiting your acceptance`;
  }
  const moment = fmtStamp(momentIso);
  return moment ? `${s.verb} ${moment}` : s.verb;
}

// E7 — the variance sign rule: cost-anchored, metric named first, on every
// Outcome and the rollup, never omitted. Three branches — the equality
// branch is not optional ("cost +$0.00 vs est" is the sign defect this rule
// exists to fix). Signs follow the COST direction: + = cost above estimate =
// unfavourable (coral-700), − = below = favourable (Ink).
export function varianceFor(actualCents, estimatedCents) {
  const d = (Number(actualCents) || 0) - (Number(estimatedCents) || 0);
  if (Math.abs(d) < 0.5) return { cls: 'flat', text: 'cost on est' };
  const abs = fmtUSDc(Math.abs(d));
  return d > 0
    ? { cls: 'bad', text: `cost +${abs} vs est` }
    : { cls: 'good', text: `cost −${abs} vs est` };
}
